<!-- A positive posting on the contact account is owed to you; negative is owed by you. -->
<script setup lang="ts">
import { formatDate, signOfMoney } from '@taler/domain'
import { ErrorState, ActionButton, EmptyState } from '@taler/ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import MoneyAmount from '@/app/components/MoneyAmount.vue'
import { RouterLink } from 'vue-router'
import type { ContactHistoryRow } from '../internal/history.ts'

const props = defineProps<{
  rows: readonly ContactHistoryRow[]
  isLoading: boolean
  isFetchingNextPage: boolean
  hasNextPage: boolean
  error: Error | null
  retrying?: boolean
  ownEntriesOnly: boolean
}>()
const emit = defineEmits<{ loadMore: []; retry: [] }>()

const { locale, t } = useI18n()
const currentLocale = computed(() => locale.value as 'en' | 'hu')

const emptyTitle = computed(() =>
  props.ownEntriesOnly ? t('contacts.historyOwnEmptyTitle') : t('contacts.historyEmptyTitle'),
)
const emptyBody = computed(() =>
  props.ownEntriesOnly ? t('contacts.historyOwnEmptyBody') : t('contacts.historyEmptyBody'),
)

const items = computed(() =>
  props.rows.map((row) => {
    const sign = signOfMoney(row.amount)
    return {
      id: row.id,
      currency: row.amount.currency,
      title: row.title,
      dateLabel: formatDate(row.date, currentLocale.value),
      amount: row.amount,
      tone: sign > 0 ? ('positive' as const) : sign < 0 ? ('negative' as const) : undefined,
    }
  }),
)
</script>

<template>
  <div class="flex flex-col">
    <p v-if="isLoading" role="status">{{ t('app.loading') }}</p>

    <ErrorState
      v-else-if="error"
      :title="t('contacts.historyErrorTitle')"
      :body="t('contacts.historyErrorBody')"
      :retry-label="t('contacts.retry')"
      :retrying="retrying"
      @retry="emit('retry')"
    />

    <EmptyState v-else-if="items.length === 0" :title="emptyTitle" :body="emptyBody"> </EmptyState>

    <template v-else>
      <ul class="flex flex-col divide-y divide-[color:var(--color-border)]">
        <li v-for="item in items" :key="`${item.id}-${item.currency}`">
          <RouterLink
            :to="{ name: 'transactions', query: { entry: item.id } }"
            class="flex items-center gap-[var(--space-cell)] px-[var(--space-inline)] h-[var(--density-row)] transition-colors duration-[var(--motion-fast)] hover:bg-[color:var(--color-surface-raised)]"
          >
            <span
              class="w-20 shrink-0 whitespace-nowrap text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]"
            >
              {{ item.dateLabel }}
            </span>
            <span
              class="min-w-0 flex-1 truncate text-[length:var(--text-step-2)] text-[color:var(--color-text)]"
            >
              {{ item.title }}
            </span>
            <span class="shrink-0">
              <MoneyAmount :value="item.amount" sign :tone="item.tone" />
            </span>
          </RouterLink>
        </li>
      </ul>
      <div v-if="hasNextPage" class="pt-3">
        <ActionButton type="button" :disabled="isFetchingNextPage" @click="emit('loadMore')">
          {{ isFetchingNextPage ? t('transactions.loadingMore') : t('transactions.loadMore') }}
        </ActionButton>
      </div>
    </template>
  </div>
</template>
